import { X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Drawer } from '@base-ui/react';
import { Alert } from '@/components/ui/alert';
import { ensureDocumentPdf, getDocumentTitle, listPages, type DocPage } from '../lib/docs';

/*
 * Self-contained right-side document viewer. Given a `docId` and an optional
 * `initialPage`, it fetches the document's title + OCR pages itself (RLS lets
 * teachers/admins read document_pages) and opens at the requested page. It
 * manages its own loading/navigation state; the parent only toggles it and
 * provides docId/initialPage.
 *
 * Rendering: prefers the REAL PDF opened at the right page (lazy-stored via the
 * ensure-document-pdf Edge Function → signed URL, fallback source_url). The OCR
 * text per page is the fallback when no PDF URL is available, and is also
 * offered via a PDF/Texte toggle.
 */
export default function DocumentDrawer({
  docId,
  initialPage,
  onClose,
}: {
  docId: string;
  initialPage?: number;
  onClose: () => void;
}) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  const [pages, setPages] = useState<DocPage[] | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);

  /* (Re)load whenever the target document or page changes. */
  useEffect(() => {
    let cancelled = false;
    setPages(null);
    setError(null);
    setPdfUrl(null);
    setShowText(false);
    /* Real PDF (lazy-stored) in parallel — best-effort, never blocks the OCR view. */
    void ensureDocumentPdf(docId).then((url) => { if (!cancelled) setPdfUrl(url); });
    Promise.all([listPages(docId), getDocumentTitle(docId)])
      .then(([p, t]) => {
        if (cancelled) return;
        setPages(p);
        setTitle(t);
        const want = initialPage ?? 1;
        const found = p.findIndex((pg) => pg.page_number === want);
        setIdx(found >= 0 ? found : 0);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [docId, initialPage]);

  const cur = pages && pages.length ? pages[Math.min(idx, pages.length - 1)] : null;

  return (
    <Drawer.Root open modal onOpenChange={(o) => { if (!o) onClose(); }} swipeDirection="right">
      <Drawer.Portal>
        <Drawer.Backdrop className="fixed inset-0 bg-[rgba(26,24,20,0.32)] z-[70]" />
        <Drawer.Popup aria-label="Aperçu du document" className="fixed top-0 right-0 h-dvh w-[min(620px,92vw)] bg-card border-l border-border shadow-[-6px_0_0_0_rgba(26,24,20,0.08)] z-[71] flex flex-col box-border pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-[10px] p-[12px_14px] border-b-[1.5px] border-[rgba(42,30,16,0.15)]">
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-[15px] whitespace-nowrap overflow-hidden text-ellipsis">
                {title ?? 'Document'}
              </div>
              {cur && (
                <div className="text-[12px] text-muted-foreground font-bold">
                  Page {cur.page_number}{pages ? ` / ${pages.length}` : ''}
                </div>
              )}
            </div>
            <button type="button" onClick={onClose} aria-label="Fermer" className="w-11 h-11 -mr-1 flex-none grid place-items-center border border-border bg-transparent rounded-[10px] cursor-pointer text-muted-foreground">
              <X size={20} weight="bold" />
            </button>
          </div>

          {/* Page navigation + PDF/Texte toggle */}
          {pages && pages.length > 0 && (
            <div className="flex items-center gap-2 p-[10px_14px] border-b-[1.5px] border-[rgba(42,30,16,0.10)]">
              <button type="button" disabled={idx <= 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} className="w-[38px] h-[38px] flex-none border border-border bg-card rounded-[8px] cursor-pointer font-extrabold font-[inherit] disabled:bg-naia-info-bg disabled:cursor-default">←</button>
              <select
                value={Math.min(idx, pages.length - 1)}
                onChange={(e) => setIdx(Number(e.target.value))}
                className="flex-1 min-w-0 py-[6px] px-2 border border-border rounded-[8px] font-[inherit] bg-naia-card-alt"
              >
                {pages.map((p, i) => (
                  <option key={i} value={i}>Page {p.page_number}</option>
                ))}
              </select>
              <button type="button" disabled={idx >= pages.length - 1} onClick={() => setIdx((i) => Math.min(pages.length - 1, i + 1))} className="w-[38px] h-[38px] flex-none border border-border bg-card rounded-[8px] cursor-pointer font-extrabold font-[inherit] disabled:bg-naia-info-bg disabled:cursor-default">→</button>
              {pdfUrl && (
                <button
                  type="button"
                  onClick={() => setShowText((v) => !v)}
                  title={showText ? 'Voir le PDF' : 'Voir le texte OCR'}
                  className="flex-none h-[38px] px-3 border border-border bg-card text-foreground rounded-[8px] font-bold text-[12px] cursor-pointer font-[inherit]"
                >
                  {showText ? '📄 PDF' : '🔤 Texte'}
                </button>
              )}
            </div>
          )}

          {/* Body */}
          {error ? (
            <div className="flex-1 min-h-0 overflow-auto p-[14px]">
              <Alert>{error}</Alert>
            </div>
          ) : pdfUrl && !showText ? (
            /* Real PDF opened at the current page. The OCR text is the inner fallback */
            /* if the browser can't embed the PDF. */
            (<object
              key={cur?.page_number ?? 1}
              data={`${pdfUrl}#page=${cur?.page_number ?? initialPage ?? 1}&view=FitH`}
              type="application/pdf"
              className="flex-1 min-h-0 w-full border-none bg-naia-card-alt"
            >
              <div className="p-[14px] text-muted-foreground">
                {`Aperçu PDF indisponible. `}<a href={pdfUrl} target="_blank" rel="noreferrer" className="text-naia-blue font-bold">Ouvrir le PDF</a>
              </div>
            </object>)
          ) : (
            <div className="flex-1 min-h-0 overflow-auto p-[14px]">
              {!pages ? (
                <div className="text-muted-foreground p-3">Chargement…</div>
              ) : pages.length === 0 ? (
                <div className="text-muted-foreground p-3">Aucune page.</div>
              ) : (
                <pre className="whitespace-pre-wrap break-words font-mono text-[13.5px] leading-relaxed m-0 text-foreground">
                  {cur?.ocr_text || '(page vide)'}
                </pre>
              )}
            </div>
          )}
        </Drawer.Popup>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
